/**
 * Date formatting and grouping utilities for word list.
 */

import { format, startOfWeek, startOfMonth, startOfYear, parseISO } from 'date-fns';

/**
 * Group words by date based on grouping option
 */
export const groupWordsByDate = (words, groupBy = 'day') => {
  const grouped = {};

  words.forEach((word) => {
    const date = parseISO(word.dateAdded);
    let groupKey;

    switch (groupBy) {
      case 'day':
        groupKey = format(date, 'dd MMMM yyyy');
        break;
      case 'week':
        const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Monday
        groupKey = `Week of ${format(weekStart, 'dd MMMM yyyy')}`;
        break;
      case 'month':
        groupKey = format(date, 'MMMM yyyy');
        break;
      case 'year':
        groupKey = format(date, 'yyyy');
        break;
      default:
        groupKey = format(date, 'dd MMMM yyyy');
    }

    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }
    grouped[groupKey].push(word);
  });

  // Sort groups by date (most recent first)
  const sortedGroups = {};
  Object.keys(grouped)
    .sort((a, b) => {
      // Get first word from each group to compare dates
      const dateA = parseISO(grouped[a][0].dateAdded);
      const dateB = parseISO(grouped[b][0].dateAdded);
      return dateB - dateA; // Descending order
    })
    .forEach((key) => {
      sortedGroups[key] = grouped[key];
    });

  return sortedGroups;
};

/**
 * Format a date string for display
 */
export const formatDate = (dateString) => {
  try {
    const date = parseISO(dateString);
    return format(date, 'dd MMMM yyyy');
  } catch (error) {
    return dateString;
  }
};

/**
 * Format a relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateString) => {
  try {
    const date = parseISO(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 1000 / 60);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} days ago`;

    if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;

    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  } catch (error) {
    return dateString;
  }
};
